import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';

import HomeIcon from '@mui/icons-material/Home';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CreateIcon from '@mui/icons-material/Create';
import ListIcon from '@mui/icons-material/List';
import PeopleIcon from '@mui/icons-material/People';

import PersonIcon from '@mui/icons-material/Person';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import { ExploreOutlined, HelpOutline } from '@mui/icons-material';

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState<string | false>(false);

  const menuItems = [
    { to: '/', label: 'ホーム', icon: <HomeIcon />, group: 'none' },
    { to: '/help/papertheater', label: 'マジスくんの劇場', icon: <ExploreOutlined />, group: 'help' },
    { to: '/tasklist', label: 'タスク一覧', icon: <ChecklistIcon />, group: 'user' },
    { to: '/select', label: 'マニュアルを選ぶ', icon: <LibraryBooksIcon />, group: 'manager' },
    { to: '/user-select', label: 'ユーザーダッシュボード', icon: <LibraryBooksIcon />, group: 'manager' },
    { to: '/admin/new', label: '新規登録', icon: <CreateIcon />, group: 'designer' },
    { to: '/admin/list', label: 'Markdown一覧', icon: <ListIcon />, group: 'designer' },
    { to: '/admin/users', label: 'ユーザー管理', icon: <PeopleIcon />, group: 'admin' },
    { to: '/admin/groups', label: 'グループ管理', icon: <ListIcon />, group: 'admin' },
  ];

  useEffect(() => {
    if (location.pathname.startsWith('/tasklist')) {
      setExpanded('user');
    } else if (location.pathname.startsWith('/select') || location.pathname.startsWith('/user-tasks')) {
      setExpanded('manager');
    } else if (location.pathname.startsWith('/admin/new') || location.pathname.startsWith('/admin/list')) {
      setExpanded('designer');
    } else if (location.pathname.startsWith('/admin/users') || location.pathname.startsWith('/admin/groups')) {
      setExpanded('admin');
    } else if (location.pathname.startsWith('/help/')) {
      setExpanded('help');
    } else {
      setExpanded(false);
    }
  }, [location.pathname]);

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const groupIcons: { [key: string]: React.ReactNode } = {
    user: <PersonIcon />,
    manager: <ManageAccountsIcon />,
    designer: <BuildIcon />,
    admin: <SecurityIcon />,
    help: <HelpOutline />,
  };

  const AccordionCard = ({ children }: { children: React.ReactNode }) => (
    <Paper
      elevation={1}
      sx={{
        mb: 1.5,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {children}
    </Paper>
  );

  const ListButton = ({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) => (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <Tooltip title={!open ? label : ''} placement="right" arrow>
        <ListItemButton
          onClick={() => navigate(to)}
          selected={location.pathname === to}
          sx={{
            minHeight: 48,
            justifyContent: open ? 'initial' : 'center',
            px: 2.5,
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: open ? 0 : '2px', // ★ アイコン切れ防止用
            }}
          >
            {icon}
          </Box>
          {open && (
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14 }} />
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  return (
    <Box sx={{ p: 1 }}>
      {/* Drawerトグルボタン */}
      <Box display="flex" justifyContent="center" mb={2}>
        <IconButton onClick={() => setOpen(!open)}>
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* メニューリスト */}
      <List sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
        {/* ホーム */}
        <AccordionCard>
          <List>
            {menuItems.filter(item => item.group === 'none').map((item) => (
              <ListButton key={item.to} to={item.to} label={item.label} icon={item.icon} />
            ))}
          </List>
        </AccordionCard>

        {/* 各区分アコーディオン */}
        {['user', 'manager', 'designer', 'admin', 'help'].map((group) => (
          <AccordionCard key={group}>
            <Accordion expanded={expanded === group} onChange={handleAccordionChange(group)}>
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      opacity: open ? 1 : 0,
                      transition: 'opacity 0.3s',
                    }}
                  />
                }
                sx={{
                  minHeight: open ? 48 : 32,
                }}
              >
                <Box display="flex" alignItems="center">
                  <Box sx={{ minWidth: open ? 36 : 48, display: 'flex', justifyContent: 'center' }}>
                    {groupIcons[group]}
                  </Box>
                  {open && (
                    <Typography variant="subtitle1" fontWeight="bold">
                      {group === 'user' && '一般利用者'}
                      {group === 'manager' && '業務管理者'}
                      {group === 'designer' && '業務設計者'}
                      {group === 'admin' && 'システム管理者'}
                      {group === 'help' && 'ヘルプ'}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List>
                  {menuItems.filter(item => item.group === group).map((item) => (
                    <ListButton key={item.to} to={item.to} label={item.label} icon={item.icon} />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </AccordionCard>
        ))}
      </List>
    </Box>
  );
};

export default Sidebar;
